import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { memo } from "react";
import Info from "~icons/material-symbols/info-outline";
import Person from "~icons/material-symbols/person-outline";
import Warning from "~icons/material-symbols/warning-outline";
import { css } from "../../../../styled-system/css";
import CheckCircle from "../../../assets/check_circle.svg";
import UserCircle from "../../../assets/user_circle_gray.svg";
import { Card } from "../../../components/card";
import Header from "../../../components/header";
import { AppError, ERROR_CODES } from "../../../libs/errors";
import { t } from "../../../libs/i18n";

type CampaignStatus = "active" | "ended" | "fulfilled";

type CampaignMessage = {
  title: string;
  description: string;
};

type InvitationDetailConfig = {
  fulfilledThreshold: number;
  progressStepCount: number;
  headingPrefix: string;
  subtitleLines: [string, string];
  shareLastLine: string;
  receiveInstructions: string[];
  warningMessages: {
    ended: CampaignMessage;
    fulfilled: CampaignMessage;
  };
  buildRewardLabel: (index: number) => string | undefined;
  buildRemainingMessage?: (remainingIntroductions: number) => string | null;
};

const CAMPAIGN_ENDED_MESSAGE: CampaignMessage = {
  title: t("キャンペーンは終了しました"),
  description: t(
    "300ptプレゼントキャンペーンは先着2万人までに達したため終了しました。",
  ),
};

const DEFAULT_RECEIVE_INSTRUCTIONS = [
  t(
    "友だち紹介画面の「招待コードを入力」欄に招待コードを入力するか、お友達のQRコードを読み取ってください。",
  ),
  t(
    "ミニアプリに登録してから1週間以内に1の手順で紹介を受けなかった場合、ポイントが付与されませんのでご注意ください。",
  ),
];

const DEFAULT_DETAIL_CONFIG: InvitationDetailConfig = {
  fulfilledThreshold: 5,
  progressStepCount: 5,
  headingPrefix: t("友だちを1名紹介ごとに"),
  subtitleLines: [t("先着2万人"), t("300pt付与は1人5回まで")],
  shareLastLine: t("1名紹介ごとに、300ポイントがプレゼントされます。"),
  receiveInstructions: DEFAULT_RECEIVE_INSTRUCTIONS,
  warningMessages: {
    ended: CAMPAIGN_ENDED_MESSAGE,
    fulfilled: {
      title: t("プレゼント上限に達しました"),
      description: t(
        "300ptプレゼント上限の5回に達したため、キャンペーン対象外となりました。",
      ),
    },
  },
  buildRewardLabel: () => "300pt",
};

const SHIGA_OTSU_PROGRESS_STEPS = 3;

const SHIGA_OTSU_DETAIL_CONFIG: InvitationDetailConfig = {
  fulfilledThreshold: SHIGA_OTSU_PROGRESS_STEPS,
  progressStepCount: SHIGA_OTSU_PROGRESS_STEPS,
  headingPrefix: t("友だちを3名紹介すると"),
  subtitleLines: [t("3名紹介で300pt"), t("300pt付与は1人1回まで")],
  shareLastLine: t(
    "3名の友だちの紹介が完了すると、300ポイントが1回プレゼントされます。",
  ),
  receiveInstructions: [
    ...DEFAULT_RECEIVE_INSTRUCTIONS,
    t("同じ友だちは1回のみ紹介特典の対象になります。"),
  ],
  warningMessages: {
    ended: CAMPAIGN_ENDED_MESSAGE,
    fulfilled: {
      title: t("プレゼント条件を達成済みです"),
      description: t(
        "3人の紹介が完了しているため、今回の300ptプレゼントは受け取り済みです。",
      ),
    },
  },
  buildRewardLabel: (index) =>
    index === SHIGA_OTSU_PROGRESS_STEPS - 1 ? "300pt" : undefined,
  buildRemainingMessage: (remainingIntroductions) =>
    remainingIntroductions > 0
      ? t("あと{count}人紹介で300ptを獲得できます。", {
          count: remainingIntroductions,
        })
      : t("紹介条件を達成しています。300ptは順次付与されます。"),
};

function getInvitationDetailConfig(tenantSlug: string): InvitationDetailConfig {
  if (tenantSlug === "shiga-otsu") {
    return SHIGA_OTSU_DETAIL_CONFIG;
  }
  return DEFAULT_DETAIL_CONFIG;
}

export const Route = createFileRoute("/_authed/invitation/detail")({
  loader: async ({ context: { client, tenant } }) => {
    const invitation = await client.getMyInvitation({}).catch((error) => {
      throw new AppError(
        ERROR_CODES.CONNECT_GET_MY_INVITATION_FAILED,
        error.message,
        {
          originalError: error,
        },
      );
    });

    let campaignStatus: CampaignStatus = "active";

    const fulfilledThreshold = getInvitationDetailConfig(
      tenant.slug,
    ).fulfilledThreshold;

    if (invitation.isCampaignOver) {
      campaignStatus = "ended";
    }

    if (invitation.invitees.length >= fulfilledThreshold) {
      campaignStatus = "fulfilled";
    }

    const { invitees } = invitation;
    return { invitees, campaignStatus };
  },
  component: InvitationDetail,
});

const ProgressBox = ({
  index,
  isCleared,
  rewardLabel,
}: {
  index: number;
  isCleared: boolean;
  rewardLabel?: string;
}) => (
  <div
    className={css({
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: "4px",
      width: "40px",
      flexShrink: 0,
    })}
  >
    <div
      className={css({
        fontSize: "12px",
        fontWeight: "600",
        color: "text.accentPrimary",
        lineHeight: "1.35",
      })}
    >
      {t("{position}人目", { position: index + 1 })}
    </div>
    <img
      src={isCleared ? CheckCircle : UserCircle}
      alt={isCleared ? t("達成") : t("未達成")}
      className={css({ width: "40px", height: "40px" })}
    />
    {rewardLabel ? (
      <div
        className={css({
          fontSize: "12px",
          fontWeight: "400",
          color: "text.secondary",
          lineHeight: "1.35",
        })}
      >
        {rewardLabel}
      </div>
    ) : null}
  </div>
);

const CampaignWarningMessage = memo(
  ({
    campaignStatus,
    messages,
  }: {
    campaignStatus: CampaignStatus;
    messages: InvitationDetailConfig["warningMessages"];
  }) => {
    if (campaignStatus === "active") {
      return null;
    }

    const message =
      campaignStatus === "ended" ? messages.ended : messages.fulfilled;

    return (
      <div
        className={css({
          bg: "surface.accentWarnLight",
          p: "16px",
          display: "flex",
          flexDirection: "column",
          gap: "8px",
        })}
      >
        <div
          className={css({
            display: "flex",
            alignItems: "center",
            gap: "8px",
            color: "text.primary",
          })}
        >
          <Warning
            className={css({
              color: "text.accentWarn",
            })}
          />
          <div>{message.title}</div>
        </div>
        <div
          className={css({
            color: "text.secondary",
            fontSize: "14px",
          })}
        >
          {message.description}
        </div>
      </div>
    );
  },
);

function InvitationDetail() {
  const { invitees, campaignStatus } = Route.useLoaderData();
  const { tenant } = Route.useRouteContext();
  const navigate = useNavigate();

  const config = getInvitationDetailConfig(tenant.slug);
  const progressStepCount = config.progressStepCount;
  const clearCount = Math.min(invitees.length, progressStepCount);
  const remainingIntroductions = Math.max(progressStepCount - clearCount, 0);
  const remainingMessage = config.buildRemainingMessage
    ? config.buildRemainingMessage(remainingIntroductions)
    : null;

  return (
    <>
      <Header
        title={t("友だち紹介キャンペーン")}
        onClickBack={() => {
          navigate({ to: "/invitation" });
        }}
      />

      <CampaignWarningMessage
        campaignStatus={campaignStatus}
        messages={config.warningMessages}
      />

      <div
        className={css({
          display: "flex",
          flexDirection: "column",
          gap: "16px",
          px: "16px",
          py: "24px",
        })}
      >
        <h1
          className={css({
            fontSize: "24px",
            fontWeight: "400",
            color: "text.primary",
            textAlign: "center",
          })}
        >
          {config.headingPrefix}
          <br />
          {t("300ptプレゼント")}
        </h1>

        <p
          className={css({
            fontSize: "16px",
            color: "text.secondary",
            textAlign: "center",
          })}
        >
          {config.subtitleLines[0]}
          <br />
          {config.subtitleLines[1]}
        </p>

        <Card>
          <div
            className={css({
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "20px",
            })}
          >
            <div
              className={css({
                fontSize: "16px",
                color: "text.primary",
                fontWeight: "400",
                lineHeight: "1.5",
                textAlign: "center",
                width: "100%",
              })}
            >
              {t("あなたの紹介ステータス")}
            </div>

            <div
              className={css({
                display: "flex",
                flexDirection: "column",
                gap: "20px",
                alignItems: "center",
                width: "100%",
              })}
            >
              <div
                className={css({
                  display: "flex",
                  justifyContent: "space-between",
                  width: "264px",
                  maxWidth: "100%",
                })}
              >
                {Array.from({ length: progressStepCount }).map((_, index) => {
                  const isCleared = index < clearCount;
                  const key = `status-${
                    isCleared ? "cleared" : "pending"
                  }-${index}`;
                  return (
                    <ProgressBox
                      key={key}
                      index={index}
                      isCleared={isCleared}
                      rewardLabel={config.buildRewardLabel(index)}
                    />
                  );
                })}
              </div>

              <Link
                to={"/invitation"}
                className={css({
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "8px",
                  bg: "surface.accentPrimary",
                  color: "white",
                  fontSize: "16px",
                  rounded: "xl",
                  px: "20px",
                  py: "8px",
                  height: "40px",
                })}
              >
                <Person />
                <span>{t("友だち紹介")}</span>
              </Link>

              {remainingMessage ? (
                <p
                  className={css({
                    fontSize: "14px",
                    color: "text.secondary",
                    textAlign: "center",
                  })}
                >
                  {remainingMessage}
                </p>
              ) : null}
            </div>
          </div>
        </Card>

        <div
          className={css({
            bg: "surface.primary",
            rounded: "xl",
            padding: "20px",
          })}
        >
          <div
            className={css({
              display: "flex",
              flexDirection: "column",
              gap: "20px",
            })}
          >
            <div
              className={css({
                display: "flex",
                alignItems: "center",
                gap: "4px",
              })}
            >
              <Info
                className={css({
                  color: "text.accentPrimary",
                  width: "24px",
                  height: "24px",
                })}
              />
              <h3
                className={css({
                  fontSize: "20px",
                  fontWeight: "600",
                  color: "text.accentPrimary",
                  lineHeight: "1.2",
                })}
              >
                {t("お友だち紹介のやり方")}
              </h3>
            </div>

            <div
              className={css({
                display: "flex",
                flexDirection: "column",
                gap: "16px",
              })}
            >
              <h4
                className={css({
                  fontSize: "16px",
                  color: "text.primary",
                  fontWeight: "700",
                  lineHeight: "1.5",
                })}
              >
                {t("お友だちに紹介する方法")}
              </h4>
              <ol
                className={css({
                  listStyle: "decimal",
                  pl: "24px",
                  lineHeight: "1.5",
                  fontSize: "16px",
                  color: "text.primary",
                })}
              >
                <li>
                  {t("本画面の上にある「友だち紹介」ボタンをタップします。")}
                </li>
                <li>
                  {t(
                    "表示される紹介コードやQRコードを、お友だちに共有してください。QRコードを送信するか、直接紹介コードを伝えることができます。",
                  )}
                </li>
                <li>
                  {t(
                    "お友だちがアプリ内で紹介コードを入力またはQRコードを読み取ってアカウントを作成すると、紹介が完了します。",
                  )}
                </li>
                <li>{config.shareLastLine}</li>
              </ol>
            </div>

            <div
              className={css({
                display: "flex",
                flexDirection: "column",
                gap: "16px",
              })}
            >
              <h4
                className={css({
                  fontSize: "16px",
                  color: "text.primary",
                  fontWeight: "700",
                  lineHeight: "1.5",
                })}
              >
                {t("紹介を受ける方法")}
              </h4>
              <ol
                className={css({
                  listStyle: "decimal",
                  pl: "24px",
                  lineHeight: "1.5",
                  fontSize: "16px",
                  color: "text.primary",
                })}
              >
                {config.receiveInstructions.map((instruction) => (
                  <li key={instruction}>{instruction}</li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
